import React, { createContext, useContext, useRef, useCallback } from 'react';
import api from '../services/api';
import { useSubscription } from './SubscriptionContext';
import { isOpeningHoursStale, calculateIsOpenNow, getOpenStatusInfo, OPENING_HOURS_CACHE_DAYS } from '../utils/openingHoursUtils';

/**
 * Fetch fresh place data via enrichment endpoint
 * This gets photos, opening hours, business status, and price level in one call
 */
async function fetchFreshPlaceData(placeId) {
  const response = await api.enrichPlace(placeId, false);
  if (!response.success) {
    throw new Error(response.error || 'Failed to enrich place');
  }
  return response.data;
}

const PlaceStatusCacheContext = createContext(null);

// In-memory cache TTL: 1 hour (session-only, reduces Firestore reads within a session)
// Firestore/stored cache TTL: 30 days (checked via isOpeningHoursStale)
// Real-time open/closed status is calculated client-side from the cached schedule
const MEMORY_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export function PlaceStatusCacheProvider({ children }) {
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const { isPremium } = useSubscription();

  const isMemoryCacheExpired = useCallback((timestamp) => {
    return Date.now() - timestamp > MEMORY_CACHE_TTL_MS;
  }, []);

  /**
   * Get opening hours for a place, using cached data when available.
   * Priority:
   * 1. In-memory cache (valid for 1 hour)
   * 2. Stored openingHours from place data (valid for 30 days)
   * 3. For premium users only: Fresh fetch from Google API
   *
   * IMPORTANT: Free users will NOT trigger API calls. They can only see opening hours
   * that were already fetched by a premium user (stored in Firestore).
   *
   * @param {string} placeId - Google Place ID
   * @param {Object} options - Options object
   * @param {Object} options.storedOpeningHours - Previously stored opening hours
   * @param {string} options.openingHoursSavedAt - When the stored data was saved
   * @param {string} options.savedAt - Fallback timestamp if openingHoursSavedAt not available
   * @param {boolean} options.forceRefresh - Force a fresh fetch from Google API
   * @returns {Promise<Object>} - Opening hours data
   */
  const getOpeningHours = useCallback(async (placeId, options = {}) => {
    const { storedOpeningHours, openingHoursSavedAt, savedAt, forceRefresh = false } = options;

    if (!placeId) {
      return Promise.reject(new Error('Place ID is required'));
    }

    // Skip caches if force refresh requested
    if (!forceRefresh) {
      // 1. Check in-memory cache first
      const cached = cacheRef.current.get(placeId);
      if (cached && !isMemoryCacheExpired(cached.timestamp)) {
        return cached.openingHours;
      }

      // 2. Check if we have valid stored opening hours (< 30 days old)
      // Use openingHoursSavedAt if available, fall back to savedAt
      const savedAtTimestamp = openingHoursSavedAt || savedAt;
      if (storedOpeningHours && savedAtTimestamp && !isOpeningHoursStale(savedAtTimestamp)) {
        // Store in memory cache
        cacheRef.current.set(placeId, {
          openingHours: storedOpeningHours,
          timestamp: Date.now(),
        });
        return storedOpeningHours;
      }
    }

    // 3. For free users, don't make API calls - return null
    // Opening hours are only fetched for premium users via enrichPlace
    if (!isPremium) {
      return null;
    }

    // 4. Check if there's already a pending request (unless force refresh)
    if (!forceRefresh) {
      const pending = pendingRef.current.get(placeId);
      if (pending) {
        return pending;
      }
    }

    // 5. Premium only: Fetch fresh via enrichment (gets photos, hours, status, price in one call)
    const promise = fetchFreshPlaceData(placeId)
      .then((data) => {
        const openingHours = data.openingHours;
        // Store in memory cache
        cacheRef.current.set(placeId, {
          openingHours,
          businessStatus: data.businessStatus,
          timestamp: Date.now(),
        });
        pendingRef.current.delete(placeId);
        return openingHours;
      })
      .catch((error) => {
        pendingRef.current.delete(placeId);
        throw error;
      });

    pendingRef.current.set(placeId, promise);
    return promise;
  }, [isMemoryCacheExpired, isPremium]);

  /**
   * Get real-time open status for a place using cached schedule
   * This calculates open/closed status client-side using the cached opening hours
   */
  const getPlaceStatus = useCallback(async (placeId, options = {}) => {
    try {
      const openingHours = await getOpeningHours(placeId, options);
      const isOpen = calculateIsOpenNow(openingHours);
      const statusInfo = getOpenStatusInfo(openingHours);

      return {
        openNow: isOpen,
        openingHours,
        statusInfo,
      };
    } catch (error) {
      return {
        openNow: null,
        openingHours: null,
        statusInfo: null,
      };
    }
  }, [getOpeningHours]);

  /**
   * Batch get opening hours for multiple places
   * Uses stored data when available, only fetches stale/missing data
   *
   * IMPORTANT: Free users will NOT trigger API calls. They can only see opening hours
   * that were already fetched by a premium user (stored in Firestore).
   */
  const batchGetOpeningHours = useCallback(async (places) => {
    if (!places || places.length === 0) {
      return {};
    }

    const results = {};
    const toFetch = [];
    const cacheStats = { memoryHit: 0, storedHit: 0, toFetch: 0 };

    // Check each place for cached/stored data
    for (const place of places) {
      if (!place.placeId) continue;

      const placeId = place.placeId;

      // Check in-memory cache
      const cached = cacheRef.current.get(placeId);
      if (cached && !isMemoryCacheExpired(cached.timestamp)) {
        cacheStats.memoryHit++;
        results[placeId] = {
          openNow: calculateIsOpenNow(cached.openingHours),
          openingHours: cached.openingHours,
          statusInfo: getOpenStatusInfo(cached.openingHours, cached.businessStatus || place.businessStatus),
          businessStatus: cached.businessStatus || place.businessStatus,
        };
        continue;
      }

      // Check stored data (30-day TTL)
      // Use openingHoursSavedAt if available, fall back to savedAt
      const savedAtTimestamp = place.openingHoursSavedAt || place.savedAt;
      if (place.openingHours && savedAtTimestamp && !isOpeningHoursStale(savedAtTimestamp)) {
        cacheStats.storedHit++;
        cacheRef.current.set(placeId, {
          openingHours: place.openingHours,
          businessStatus: place.businessStatus,
          timestamp: Date.now(),
        });
        results[placeId] = {
          openNow: calculateIsOpenNow(place.openingHours),
          openingHours: place.openingHours,
          statusInfo: getOpenStatusInfo(place.openingHours, place.businessStatus),
          businessStatus: place.businessStatus,
        };
        continue;
      }

      // Skip fetching only if we have definitively checked and the place has no hours
      // openingHoursSavedAt being set (even if openingHours is null) means we checked
      // If openingHoursSavedAt is null, we've never fetched hours for this place
      const hasBeenCheckedForHours = place.openingHoursSavedAt != null;
      const hasNoHoursAndWasChecked = !place.openingHours && hasBeenCheckedForHours && !isOpeningHoursStale(place.openingHoursSavedAt);
      if (hasNoHoursAndWasChecked) {
        cacheStats.skippedNoHours = (cacheStats.skippedNoHours || 0) + 1;
        // Return result with businessStatus even if no opening hours (for permanently/temporarily closed)
        results[placeId] = {
          openNow: null,
          openingHours: null,
          statusInfo: getOpenStatusInfo(null, place.businessStatus),
          businessStatus: place.businessStatus,
        };
        continue;
      }

      // Need to fetch - but only for premium users
      // Also skip if already pending (prevents duplicate requests from race conditions)
      if (isPremium && !pendingRef.current.has(placeId)) {
        cacheStats.toFetch++;
        toFetch.push(place);
      } else if (pendingRef.current.has(placeId)) {
        // Place is already being fetched, skip
      } else {
        // Free user - don't fetch, but still show businessStatus if available
        cacheStats.skippedFreeUser = (cacheStats.skippedFreeUser || 0) + 1;
        results[placeId] = {
          openNow: null,
          openingHours: null,
          statusInfo: getOpenStatusInfo(null, place.businessStatus),
          businessStatus: place.businessStatus,
        };
      }
    }

    // Log cache performance (only if fetching or in development)
    if (toFetch.length > 0 || process.env.NODE_ENV === 'development') {
      console.log('[PlaceStatusCache] Batch check:', {
        total: places.length,
        memoryHit: cacheStats.memoryHit,
        storedHit: cacheStats.storedHit,
        skippedNoHours: cacheStats.skippedNoHours || 0,
        toFetch: cacheStats.toFetch,
      });
    }

    // Fetch missing/stale data via enrichment API (Premium only)
    // This gets photos, opening hours, business status, and price level in one Enterprise call
    // Backend has a limit of 100 places per batch, so we need to chunk the requests
    if (toFetch.length > 0) {
      const BATCH_SIZE = 100;
      const chunks = [];
      for (let i = 0; i < toFetch.length; i += BATCH_SIZE) {
        chunks.push(toFetch.slice(i, i + BATCH_SIZE));
      }

      // Mark all places as pending to prevent duplicate requests
      const fetchingPlaceIds = toFetch.map(p => p.placeId);
      fetchingPlaceIds.forEach(id => pendingRef.current.set(id, true));

      try {
        // Process all chunks in parallel using enrichment API
        const chunkPromises = chunks.map(async (chunk) => {
          const placeIds = chunk.map(p => p.placeId);
          return api.batchEnrichPlaces(placeIds, false);
        });

        const responses = await Promise.all(chunkPromises);

        // Merge results from all chunks
        let fetchedCount = 0;
        let withHoursCount = 0;
        responses.forEach((response, idx) => {
          // Log any errors from the API
          if (response.data?.errors && response.data.errors.length > 0) {
            console.warn(`[PlaceStatusCache] Chunk ${idx} errors:`, response.data.errors);
          }

          // Process enriched places data returned directly from the API
          if (response.success && response.data?.places) {
            Object.entries(response.data.places).forEach(([placeId, placeData]) => {
              fetchedCount++;
              const openingHours = placeData.openingHours;
              const businessStatus = placeData.businessStatus;
              if (openingHours) withHoursCount++;
              // Store in memory cache
              cacheRef.current.set(placeId, {
                openingHours,
                businessStatus,
                timestamp: Date.now(),
              });
              results[placeId] = {
                openNow: calculateIsOpenNow(openingHours),
                openingHours,
                statusInfo: getOpenStatusInfo(openingHours, businessStatus),
                businessStatus,
              };
            });
          }
        });

        console.log('[PlaceStatusCache] Batch enrichment completed:', {
          requested: toFetch.length,
          fetched: fetchedCount,
          withOpeningHours: withHoursCount,
        });
      } catch (error) {
        console.error('[PlaceStatusCache] Batch enrichment failed:', error);
        // Fallback: results stay empty for failed places
      } finally {
        // Clear pending status for all fetched places
        fetchingPlaceIds.forEach(id => pendingRef.current.delete(id));
      }
    }

    return results;
  }, [isMemoryCacheExpired, isPremium]);

  /**
   * Legacy method for backwards compatibility
   * Returns status in the old format
   */
  const batchGetPlaceStatuses = useCallback(async (placeIds) => {
    if (!placeIds || placeIds.length === 0) {
      return {};
    }

    // Convert placeIds to place objects (no stored data)
    const places = placeIds.map(placeId => ({ placeId }));
    const results = await batchGetOpeningHours(places);

    // Convert to old format
    const oldFormatResults = {};
    Object.entries(results).forEach(([placeId, status]) => {
      oldFormatResults[placeId] = {
        openNow: status.openNow,
        openingHours: status.openingHours,
      };
    });

    return oldFormatResults;
  }, [batchGetOpeningHours]);

  /**
   * Check if stored opening hours need refresh (stale after 30 days)
   */
  const needsOpeningHoursRefresh = useCallback((openingHoursSavedAt) => {
    return isOpeningHoursStale(openingHoursSavedAt);
  }, []);

  /**
   * Force refresh opening hours for a specific place
   * Use this when user manually requests a refresh
   * Premium only - free users cannot trigger opening hours fetches
   * @param {string} placeId - Google Place ID
   * @returns {Promise<Object>} - Fresh opening hours data
   */
  const refreshOpeningHours = useCallback(async (placeId) => {
    if (!placeId) {
      return Promise.reject(new Error('Place ID is required'));
    }

    // Free users cannot trigger opening hours fetches
    if (!isPremium) {
      return null;
    }

    // Clear from memory cache
    cacheRef.current.delete(placeId);

    // Fetch fresh from API
    return getOpeningHours(placeId, { forceRefresh: true });
  }, [getOpeningHours, isPremium]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const getCacheStats = useCallback(() => {
    const cache = cacheRef.current;
    const pending = pendingRef.current;
    let validEntries = 0;
    let expiredEntries = 0;

    cache.forEach((entry) => {
      if (isMemoryCacheExpired(entry.timestamp)) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    });

    return {
      totalCached: cache.size,
      validEntries,
      expiredEntries,
      pendingRequests: pending.size,
    };
  }, [isMemoryCacheExpired]);

  const value = {
    getOpeningHours,
    getPlaceStatus,
    batchGetOpeningHours,
    batchGetPlaceStatuses, // Legacy support
    needsOpeningHoursRefresh,
    refreshOpeningHours, // Manual refresh for user-triggered updates
    clearCache,
    getCacheStats,
    // Export utils for direct use
    calculateIsOpenNow,
    getOpenStatusInfo,
    // Export TTL constant for UI display
    OPENING_HOURS_CACHE_DAYS,
  };

  return (
    <PlaceStatusCacheContext.Provider value={value}>
      {children}
    </PlaceStatusCacheContext.Provider>
  );
}

export function usePlaceStatusCache() {
  const context = useContext(PlaceStatusCacheContext);
  if (!context) {
    throw new Error('usePlaceStatusCache must be used within a PlaceStatusCacheProvider');
  }
  return context;
}

export default PlaceStatusCacheContext;
